import React from "react";

import { TrelloLogo } from "~/integrations/trello/TrelloLogo";
import { ReactComponent as AirtableIcon } from "~/legacy-assets/images/logos/Airtable.svg";
import { ReactComponent as FigmaIcon } from "~/legacy-assets/images/logos/Figma.svg";
import { ReactComponent as GiphyIcon } from "~/legacy-assets/images/logos/Giphy.svg";
import { ReactComponent as GoogleDocsIcon } from "~/legacy-assets/images/logos/google_docs.svg";
import { ReactComponent as GoogleSheetsIcon } from "~/legacy-assets/images/logos/google_sheets.svg";
import { ReactComponent as GoogleSlidesIcon } from "~/legacy-assets/images/logos/google_slides.svg";
import { ReactComponent as LoomIcon } from "~/legacy-assets/images/logos/loom-icon.svg";
import { ReactComponent as LucidIcon } from "~/legacy-assets/images/logos/Lucid.svg";
import { ReactComponent as MiroIcon } from "~/legacy-assets/images/logos/Miro.svg";
import { ReactComponent as SmartsheetIcon } from "~/legacy-assets/images/logos/Smartsheet.svg";
import { ReactComponent as SpotifyIcon } from "~/legacy-assets/images/logos/Spotify.svg";
import { ReactComponent as VidyardIcon } from "~/legacy-assets/images/logos/vidyard.svg";
import { ReactComponent as VimeoIcon } from "~/legacy-assets/images/logos/Vimeo.svg";
import { ReactComponent as YoutubeIcon } from "~/legacy-assets/images/logos/Youtube.svg";
import { capitalizeFirstLetter } from "~/lib/utils";

import { INVALID_URL } from "./EmbedModal";
import { type EmbedNodeAttributes, type EmbedObject, getSpotifyTypeAndId } from "./helpers";

const createEmbedObject = ({
	name,
	domains,
	icon,
	getEmbedNodeAttributes: deriveEmbedAttrs,
	autoEmbed,
}: Omit<EmbedObject, "urlIsSupported">): EmbedObject => ({
	name,
	domains,
	icon,
	autoEmbed,
	async getEmbedNodeAttributes(url) {
		if (!this.urlIsSupported(url)) return new Error(INVALID_URL);

		return deriveEmbedAttrs(url);
	},
	urlIsSupported(url) {
		const { pathname, host } = url;
		const simplifiedURL = host + pathname;
		return domains.some(domain => simplifiedURL.includes(domain));
	},
});

const figma = createEmbedObject({
	name: "Figma",
	domains: ["figma.com"],
	icon: <FigmaIcon />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		const regexTest = url
			.toString()
			.match(
				/https:\/\/(?:[\w.-]+\.)?figma.com\/(?:file|proto|design|board)\/([\da-zA-Z]{22,128})\/?([\da-zA-Z-]*).*$/,
			);

		if (regexTest === null) {
			return new Error("That Figma link cannot be embedded. Try another link.");
		}

		const embedUrl = `https://www.figma.com/embed?embed_host=fellow&url=${encodeURIComponent(url.toString())}`;
		return {
			service: "figma",
			originalUrl: url.href,
			embedUrl,
			embedId: regexTest[1],
			title: regexTest[2]?.replace("-", " "),
		};
	},
});

const miro = createEmbedObject({
	name: "Miro",
	domains: ["miro.com"],
	icon: <MiroIcon />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		const regexResult = url.toString().match(/https:\/\/miro.com\/app\/board\/([^/]*)(?:\/.*)?$/);

		if (regexResult === null) {
			return new Error("That Miro link cannot be embedded. Try another link.");
		}

		const boardId = regexResult[1];
		return {
			service: "miro",
			originalUrl: url.href,
			embedUrl: `https://miro.com/app/live-embed/${boardId}/?embedAutoplay=true`,
			embedId: boardId,
		};
	},
});

const vimeo = createEmbedObject({
	name: "Vimeo",
	domains: ["vimeo.com"],
	icon: <VimeoIcon />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		try {
			const response = await fetch(`https://vimeo.com/api/oembed.json?url=${url}`);

			if (response.status === 403) return new Error("Embed permissions for this video are disabled.");
			if (response.status !== 200) return new Error("That Vimeo link cannot be embedded. Try another link.");

			const { title, video_id: videoId } = await response.json();

			return {
				service: "vimeo",
				originalUrl: url.href,
				embedUrl: `https://player.vimeo.com/video/${videoId}`,
				embedId: videoId,
				title,
			};
		} catch {
			return new Error("Could not get Vimeo video. Please try again later.");
		}
	},
});

const youtube = createEmbedObject({
	name: "YouTube",
	domains: ["youtube.com", "youtu.be"],
	icon: <YoutubeIcon />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		const invalidLink = "That YouTube link cannot be embedded. Try another link.";
		const invalidStart = "The start time for this video is invalid";
		const invalidVideo = "Please enter a URL for a YouTube video.";
		const fetchFailed = "Could not get YouTube video. Please try again later.";

		if (!url) return Error(invalidLink);

		const createNodeAttributes = async (videoId: string, startTime: string): Promise<EmbedNodeAttributes | Error> => {
			const nodeAttrs: EmbedNodeAttributes = {
				service: "youtube",
				originalUrl: url.href,
				embedUrl: `https://www.youtube.com/embed/${videoId}?start=${startTime}&origin=${window.location.origin}`,
				embedId: videoId,
			};

			try {
				const response = await fetch(`https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(url.href)}`);

				if (response.status === 200) {
					const { title } = await response.json();
					nodeAttrs.title = title;
				}

				return nodeAttrs;
			} catch (error) {
				return new Error(fetchFailed);
			}
		};

		switch (url.hostname) {
			case "www.youtube.com":
			case "youtube.com":
				// Example urls
				// - https://www.youtube.com/embed/G18FtojhHzo
				// - https://www.youtube.com/embed/G18FtojhHzo?start=1547
				if (url.pathname.includes("embed")) {
					const startTime = url.searchParams.get("start") || "0";

					if (isNaN(parseInt(startTime))) {
						return new Error(invalidStart);
					}

					const videoId = url.pathname.split("/")[2];

					if (!videoId) {
						return new Error(invalidLink);
					}

					return createNodeAttributes(videoId, startTime);
				}

				// Example urls
				// - https://www.youtube.com/watch?v=G18FtojhHzo
				// - https://www.youtube.com/watch?v=G18FtojhHzo&t=987s
				if (url.pathname.includes("watch")) {
					const startTimeWithS = url.searchParams.get("t") || "0s";
					const startTime = startTimeWithS.slice(0, -1);

					if (isNaN(parseInt(startTime))) {
						return new Error(invalidStart);
					}

					const videoId = url.searchParams.get("v");

					if (!videoId) {
						return new Error(invalidLink);
					}

					return createNodeAttributes(videoId, startTime);
				}

				return new Error(invalidVideo);
			case "youtu.be": {
				// Example urls
				// - https://youtu.be/G18FtojhHzo
				// - https://youtu.be/G18FtojhHzo?t=969
				const startTime = url.searchParams.get("t") || "0";

				if (isNaN(parseInt(startTime))) {
					return new Error(invalidStart);
				}

				const videoId = url.pathname.split("/")[1];

				if (!videoId) {
					return new Error(invalidLink);
				}

				return createNodeAttributes(videoId, startTime);
			}
			default:
				return new Error(invalidLink);
		}
	},
});

const spotify = createEmbedObject({
	name: "Spotify",
	domains: ["open.spotify.com"],
	icon: <SpotifyIcon />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		const typeAndId = getSpotifyTypeAndId(url);
		if (!typeAndId) {
			return new Error("That Spotify link cannot be embedded. Try another link.");
		}
		const contentType = typeAndId.type; //playlist, track, or album
		const contentId = typeAndId.id;

		return {
			service: "spotify",
			originalUrl: url.href,
			embedUrl: `https://open.spotify.com/embed/${contentType}/${contentId}`,
			title: capitalizeFirstLetter(contentType),
			embedId: contentId,
		};
	},
});

const giphy = createEmbedObject({
	name: "Giphy",
	domains: ["media.giphy.com", "giphy.com"],
	icon: <GiphyIcon />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		/*
			Example URL formats matched by regex
			- https://media.giphy.com/media/Ju7l5y9osyymQ/giphy.gif
			- https://giphy.com/gifs/rick-astley-Ju7l5y9osyymQ
		*/
		const regexResult = url
			.toString()
			.match(/https:\/\/(?:media.)?giphy.com\/(?:gifs|media)\/(?:[-\w]+-)?(\w+)(?:\/giphy.gif)?/);

		if (regexResult === null) {
			return new Error("That Giphy link cannot be embedded. Try another link.");
		}

		const gifId = regexResult[1];

		return {
			service: "giphy",
			originalUrl: url.href,
			embedUrl: `https://media.giphy.com/media/${gifId}/giphy.gif`,
			embedId: gifId,
		};
	},
});

const loom = createEmbedObject({
	name: "Loom",
	domains: ["loom.com"],
	icon: <LoomIcon />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		const regexTest = url.toString().match(/https:\/\/(?:[\w.-]+\.)?loom.com\/share\/([\da-zA-Z]*)(?:[?/].*)?$/);

		if (regexTest === null) {
			return new Error("That Loom link cannot be embedded. Try another link.");
		}

		const videoId = regexTest[1];

		try {
			const response = await fetch(`https://www.loom.com/v1/oembed?url=${url}`);

			if (response.status !== 200) return new Error("Could not get Loom video. Check that your link is correct.");

			const { title } = await response.json();

			return {
				service: "loom",
				originalUrl: url.href,
				embedUrl: `https://www.loom.com/embed/${videoId}`,
				embedId: videoId,
				title,
			};
		} catch {
			return new Error("Could not get Loom video. Please try again later.");
		}
	},
});

const googleslides = createEmbedObject({
	name: "Google Slides",
	domains: ["docs.google.com/presentation"],
	icon: <GoogleSlidesIcon />,
	autoEmbed: false,
	async getEmbedNodeAttributes(url) {
		const regexResult = url.toString().match(/https:\/\/([\w.-]+\.)?docs.google.com\/presentation\/d\/(.*?)(?:\/.*)?$/);

		if (regexResult === null) {
			return new Error("That Google Slides link cannot be embedded. Try another link.");
		}

		const documentId = regexResult[2];

		return {
			service: "googleslides",
			originalUrl: url.href,
			embedUrl: `https://docs.google.com/presentation/d/${documentId}/preview`,
			embedId: documentId,
		};
	},
});

const googlesheets = createEmbedObject({
	name: "Google Sheets",
	domains: ["docs.google.com/spreadsheets"],
	icon: <GoogleSheetsIcon />,
	autoEmbed: false,
	async getEmbedNodeAttributes(url) {
		const regexResult = url.toString().match(/https:\/\/([\w.-]+\.)?docs.google.com\/spreadsheets\/d\/(.*?)(?:\/.*)?$/);

		if (regexResult === null) {
			return new Error("That Google Sheets link cannot be embedded. Try another link.");
		}

		const documentId = regexResult[2];

		return {
			service: "googlesheets",
			originalUrl: url.href,
			embedUrl: `https://docs.google.com/spreadsheets/d/${documentId}/preview`,
			embedId: documentId,
		};
	},
});

const googledocs = createEmbedObject({
	name: "Google Docs",
	domains: ["docs.google.com/document/"],
	icon: <GoogleDocsIcon />,
	autoEmbed: false,
	async getEmbedNodeAttributes(url) {
		const regexResult = url.toString().match(/https:\/\/([\w.-]+\.)?docs.google.com\/document\/d\/(.*?)(?:\/.*)?$/);

		if (regexResult === null) {
			return new Error("That Google Docs link cannot be embedded. Try another link.");
		}

		const documentId = regexResult[2];

		return {
			service: "googledocs",
			originalUrl: url.href,
			embedUrl: `https://docs.google.com/document/d/${documentId}/preview`,
			embedId: documentId,
		};
	},
});

const vidyard = createEmbedObject({
	name: "Vidyard",
	domains: ["share.vidyard.com/watch", "play.vidyard.com"],
	icon: <VidyardIcon />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		const regexResult = url
			.toString()
			.match(/https:\/\/(?:share\.vidyard\.com\/watch|play\.vidyard\.com)\/(.*?)(?:.html)?(?:\/.*|\?)?$/);

		if (regexResult === null) {
			return new Error("That Vidyard link cannot be embedded. Try another link.");
		}

		const videoId = regexResult[1];

		return {
			service: "vidyard",
			originalUrl: url.href,
			embedUrl: `https://play.vidyard.com/${videoId}.html?`,
			embedId: videoId,
		};
	},
});

const lucid = createEmbedObject({
	name: "Lucid",
	domains: ["lucid.app/lucidspark", "lucid.app/lucidchart"],
	icon: <LucidIcon width="12px" />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		const regexResult = url.toString().match(/https:\/\/lucid.app\/(lucidspark|lucidchart)\/([a-zA-Z\d\-?]+)\/?/);

		if (regexResult === null) {
			return new Error("That Lucid link cannot be embedded. Try another link.");
		}

		const chartId = regexResult[2];

		return {
			service: "lucid",
			originalUrl: url.href,
			embedUrl: `https://lucid.app/documents/embeddedchart/${chartId}`,
			embedId: chartId,
		};
	},
});

const trello = createEmbedObject({
	name: "Trello",
	domains: ["trello.com"],
	icon: <TrelloLogo style={{ width: "auto" }} />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		const regexResult = url.toString().match(/https:\/\/trello.com\/b\/([a-zA-Z\d]+)\/?/);

		if (regexResult === null) {
			return new Error("That Trello link cannot be embedded. Try another link.");
		}

		const boardId = regexResult[1];

		return {
			service: "trello",
			originalUrl: url.href,
			embedUrl: `https://trello.com/b/${boardId}.html`,
			embedId: boardId,
		};
	},
});

const airtable = createEmbedObject({
	name: "Airtable",
	domains: ["airtable.com/shr", "airtable.com/embed"],
	icon: <AirtableIcon width="12px" />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		const regexResult = url.toString().match(/https:\/\/airtable.com\/(shr[a-zA-Z\d]+|embed\/[a-zA-Z\d]+)\/?(.*$)/);

		if (regexResult === null) {
			return new Error("That Airtable link cannot be embedded. Try another link.");
		}

		let viewId = regexResult[1];
		let restOfUrl = regexResult[2];

		let embedUrl = url.toString();

		// this is for the Airtable urls that don't have embed/ in their urls (e.g. https://airtable.com/shr)
		if (!viewId.startsWith("embed/")) {
			embedUrl = `https://airtable.com/embed/${viewId}${restOfUrl ? `/${restOfUrl}` : ""}`;
		}

		return {
			service: "airtable",
			originalUrl: url.href,
			embedUrl,
			embedId: viewId,
		};
	},
});

const smartsheet = createEmbedObject({
	name: "Smartsheet",
	domains: ["app.smartsheet.com", "publish.smartsheet.com"],
	icon: <SmartsheetIcon width="12px" />,
	autoEmbed: true,
	async getEmbedNodeAttributes(url) {
		const regexResult = url.toString().match(/https:\/\/((publish.smartsheet.com)|(app.smartsheet.com\/b\/publish))/);

		if (regexResult === null) {
			return new Error("That Smartsheet link cannot be embedded. Try another link.");
		}

		return {
			service: "smartsheet",
			originalUrl: url.href,
			embedUrl: url.toString(),
			embedId: url.toString(),
		};
	},
});

export default {
	figma,
	miro,
	vimeo,
	youtube,
	spotify,
	giphy,
	loom,
	googleslides,
	googlesheets,
	googledocs,
	vidyard,
	lucid,
	trello,
	airtable,
	smartsheet,
};
